import { AddContractSteps } from 'features/contracts/add/types.ts'
import contractStyles from 'features/contracts/Contracts.module.scss'
import styles from './ReferralStep.module.scss'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import FormButtons from 'features/contracts/components/form-buttons/FormButtons.tsx'
import parse from 'html-react-parser'
import { EMAIL } from 'constants/regex.ts'
import { ReferralFormInputs, ReferralStepProps } from './types.ts'
import { Form, Heading, TextField, InlineMessage } from '@boltenergy-be/design-system'
import { ContractFlowTrackingTypes } from 'types/tracking.ts'
import Phone from 'components/form-fields/phone/Phone.tsx'

const ReferralStep = ({ setCurrentStep, setNextStep, defaultValues, isTerminate }: ReferralStepProps) => {
  // i18n
  const { t } = useTranslation(['contracts', 'commonFormFields', 'validation'])

  // React Hook Form
  const hookForm = useForm<ReferralFormInputs>({
    defaultValues,
    mode: 'onBlur'
  })
  const { register, handleSubmit, watch, formState } = hookForm
  const { errors } = formState
  const watchAllFields = watch()
  const isFormEmpty = Object.values(watchAllFields).every((input) => !input)

  /**
   * Handle form submission, only save the inputs if there are any given
   * @param {ReferralFormInputs} inputs
   */
  const onSubmit = (inputs: ReferralFormInputs) => {
    if (Object.values(inputs).every((input) => !input)) {
      setCurrentStep()
    } else {
      setNextStep(inputs)
    }
  }

  return (
    <section className={styles['referral-step']}>
      <header>
        <Heading as="h1" variant="h4" className={contractStyles['referral-title']}>
          {parse(t(($) => (isTerminate ? $.terminate.referral : $.add.steps.referral).title))}
        </Heading>
        <p className="mt-200">{t(($) => (isTerminate ? $.terminate.referral : $.add.steps.referral).description)}</p>
      </header>

      <FormProvider {...hookForm}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* FIRST NAME */}
          <TextField
            id="firstName"
            label={t(($) => (isTerminate ? $.terminate.referral : $.add.steps.referral).form.firstName)}
            {...register('firstName', { required: !isFormEmpty })}
            error={errors?.firstName?.message}
            grid="full"
          />

          {/* LAST NAME */}
          <TextField
            id="lastName"
            label={t(($) => (isTerminate ? $.terminate.referral : $.add.steps.referral).form.lastName)}
            {...register('lastName', { required: !isFormEmpty })}
            error={errors?.lastName?.message}
            grid="full"
          />

          {/* EMAIL */}
          <TextField
            id="email"
            label={t(($) => (isTerminate ? $.terminate.referral : $.add.steps.referral).form.email)}
            {...register('email', {
              pattern: {
                value: EMAIL,
                message: t(($) => $.invalid.email, {
                  ns: 'validation'
                })
              },
              required: !isFormEmpty
            })}
            error={errors?.email?.message}
            type="email"
            grid="full"
          />

          {/* PHONE */}
          <Phone
            label={t(($) => $.phone, {
              ns: 'commonFormFields'
            })}
            grid="full"
            required={false}
          />

          {errors && Object.values(errors)?.some(({ type }) => type === 'required') && (
            <InlineMessage type="negative" className="grid-col-full">
              {t(($) => $.invalid.requiredWhenNotAllEmpty, {
                ns: 'validation'
              })}
            </InlineMessage>
          )}

          <FormButtons
            currentStep={AddContractSteps.REFERRAL}
            trackingId={isTerminate ? ContractFlowTrackingTypes.TERMINATE : ContractFlowTrackingTypes.ADD}
          />
        </Form>
      </FormProvider>
    </section>
  )
}

export default ReferralStep
